import { CommonModule, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { ApiError, HttpService } from '../../../services/http.service';
import { UserProfileService } from '../../../services/user-profile.service';
import { InputComponent } from '../../from-input/input.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';

interface EthnicityData {
  id: string;
  ethnicityName: string;
  color: string;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: EthnicityData;
}

@Component({
  selector: 'app-ethnicity-dialog',
  standalone: true,
  templateUrl: './add-ethnicity.component.html',
  styleUrls: ['./add-ethnicity.component.scss'],
  imports: [CommonModule, NgIf, ReactiveFormsModule, InputComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AddEthnicityDialogComponent implements OnInit {
  form!: FormGroup;
  loading = false;
  isEdit: boolean = false;
  viewMode: boolean = false;
  isProcessing = false;
  selectedColor: string = '#000000';

  ethnicityData: EthnicityData | null = null;

  readonly customErrorMessages = {
    ethnicityName: {
      required: 'Ethnicity is required.',
      minlength: 'Ethnicity name must be at least 2 characters.',
      maxlength: 'Ethnicity name cannot exceed 50 characters.',
    },
  };

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<AddEthnicityDialogComponent>,
    private dialog: MatDialog,
    private userProfileService: UserProfileService,
    @Inject(MAT_DIALOG_DATA) public data: EthnicityData | null,
  ) {
    this.initForm();

    if (data && data.id) {
      this.isEdit = true;
      this.ethnicityData = data;
      this.selectedColor = data.color || '#000000';
    }
  }

  ngOnInit(): void {
    if (this.isEdit && this.ethnicityData) {
      this.patchFormWithData();
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      ethnicityName: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
        ],
      ],
      color: ['#000000'],
    });
  }

  private patchFormWithData(): void {
    if (this.ethnicityData) {
      this.form.patchValue({
        ethnicityName: this.ethnicityData.ethnicityName || '',
        color: this.ethnicityData.color || '#000000',
      });
      if (this.viewMode) {
        this.form.disable();
      }

      this.cdr.detectChanges();
    }
  }

  onColorChange(event: any): void {
    this.selectedColor = event.target.value;
    this.form.get('color')?.setValue(this.selectedColor);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSave(): void {
    if (this.loading || this.isProcessing || this.viewMode) {
      return;
    }
    this.isProcessing = true;
    this.loading = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.loading = false;
      this.isProcessing = false;
      return;
    }

    const payload = {
      ethnicity_name: this.form.get('ethnicityName')?.value?.trim(),
      ethnicity_color: this.form.get('color')?.value || '#50B748',
    };

    const endpoint = this.isEdit
      ? `settings/ethnicities/${this.ethnicityData?.id}`
      : `settings/ethnicities`;

    const request = this.isEdit
      ? this.httpService.put<ApiDataResponse>(endpoint, payload)
      : this.httpService.post<ApiDataResponse>(endpoint, payload);

    request
      .pipe(
        finalize(() => {
          this.loading = false;
          this.isProcessing = false;
          this.form.enable();
        }),
      )
      .subscribe({
        next: (response) => this.handleSubmitResponse(response),
        error: (err: ApiError) => this.handleError(err),
      });
  }

  private handleSubmitResponse(response: ApiDataResponse): void {
    if (response.status) {
      const message = this.isEdit
        ? 'Ethnicity updated successfully'
        : 'Ethnicity added successfully';

      this.dialogRef.close({
        success: true,
        data: response.data,
      });

      this.showSuccessToast(response.message || message);
    } else {
      this.showErrorToast(
        response.message || 'Something went wrong. Please try again later.',
      );
    }
  }

  private handleError(err: ApiError): void {
    this.loading = false;
    this.isProcessing = false;
    if (err?.errors && typeof err.errors === 'object') {
      Object.values(err.errors).forEach((fieldErrors: any) => {
        if (Array.isArray(fieldErrors)) {
          fieldErrors.forEach((message) => {
            this.showErrorToast(message);
          });
        } else if (typeof fieldErrors === 'string') {
          this.showErrorToast(fieldErrors);
        }
      });
      return;
    }

    const errorMessage = this.getErrorMessage(err);
    this.showErrorToast(errorMessage);
  }

  private getErrorMessage(err: any): string {
    if (err?.message) return err.message;
    if (err?.status === 409) return 'Ethnicity already exists.';
    if (err?.status === 400)
      return 'Invalid data provided. Please check your input.';
    if (err?.status === 500) return 'Server error. Please try again later.';
    return 'Something went wrong. Please try again later.';
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type: 'success' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;

    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type: 'error' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['error-toast'],
    });
  }
}